import { hasValidPrice } from '@hedinbil/hedin-ui/dist/utils/hasValidPrice';
import { formatPrice } from '@hedinbil/hedin-ui/dist/utils/formatPrice';

export const FINANCE_PRIVATE_LEASING = 'PRIVATE_LEASING';
export const FINANCE_RESERVATION = 'RESERVATION';
export const FINANCE_CASH = 'CASH';
export const FINANCE_SMART_LOAN = 'SMART_LOAN';
export const FINANCE_COMPANY_LEASING = 'COMPANY_LEASING';

export const createFinanceArray = (carData) => {
  const choices = [];

  if (carData?.canBeReserved) {
    choices.push({
      name: carData?.reservation?.title,
      description: carData?.reservation?.subTitle,
      price: formatPrice(3000),
      priceSuffix: 'kr',
      type: FINANCE_RESERVATION,
    });
  }

  if (
    carData?.privateLeasing &&
    carData.privateLeasing?.title &&
    hasValidPrice(carData.privateLeasing?.price)
  ) {
    const description = carData.privateLeasing?.subTitle;
    choices.push({
      name: carData.privateLeasing?.title,
      type: FINANCE_PRIVATE_LEASING,
      price: formatPrice(carData.privateLeasing?.price),
      priceSuffix: 'kr/mån',
      description: description ? description : '',
    });
  }

  if (carData?.smartLoan && carData.smartLoan?.title && hasValidPrice(carData.smartLoan?.price)) {
    const description = carData.smartLoan?.subTitle;
    choices.push({
      name: carData.smartLoan?.title,
      type: FINANCE_SMART_LOAN,
      price: formatPrice(carData.smartLoan?.price),
      priceSuffix: 'kr/mån',
      description: description ? description : '',
    });
  }

  if (carData?.original) {
    const description = carData.cashPayment?.subTitle;
    const buyOutPrice = carData?.buyOut;

    const newPrice = hasValidPrice(buyOutPrice) ? buyOutPrice : carData.original;

    choices.push({
      name: carData.cashPayment?.title,
      type: FINANCE_CASH,
      price: formatPrice(newPrice),
      priceSuffix: 'kr',
      description: description ? description : '',
    });
  }

  if (
    carData?.companyLeasing &&
    carData.companyLeasing?.title &&
    hasValidPrice(carData.companyLeasing?.price)
  ) {
    const description = carData.companyLeasing?.subTitle;
    choices.push({
      name: carData.companyLeasing?.title,
      type: FINANCE_COMPANY_LEASING,
      price: formatPrice(carData.companyLeasing?.price),
      priceSuffix: 'kr/mån',
      description: description ? description : '',
    });
  }

  return choices;
};

export const getTotalPrice = (type, carData, suffix) => {
  switch (type) {
    case FINANCE_PRIVATE_LEASING:
      return carData.privateLeasing?.price + (suffix ? ' kr/mån (inkl. moms)' : '');

    case FINANCE_COMPANY_LEASING:
      return carData.companyLeasing?.price + (suffix ? ' kr/mån (exkl. moms)' : '');

    case FINANCE_RESERVATION:
      return formatPrice(3000) + (suffix ? ' kr (inkl. moms)' : '');

    case FINANCE_SMART_LOAN:
      return carData.smartLoan?.price + (suffix ? ' kr/mån (inkl. moms)' : '');

    case FINANCE_CASH:
      const buyOutPrice = carData?.buyOut;
      const newPrice = hasValidPrice(buyOutPrice) ? buyOutPrice : carData.original;
      return newPrice + (suffix ? ' kr' : '');

    default:
      return 'N/A';
  }
};

const getPriceTypeFromCheckoutType = (checkoutType) => {
  switch (checkoutType) {
    case FINANCE_PRIVATE_LEASING:
      return 'monthlyCost';

    case FINANCE_COMPANY_LEASING:
      return 'monthlyCost';

    case FINANCE_SMART_LOAN:
      return 'monthlyCost';

    default:
      return 'price';
  }
};

export const getPriceSuffix = (checkoutType) => {
  switch (checkoutType) {
    case FINANCE_PRIVATE_LEASING:
      return ' kr/mån (inkl. moms)';

    case FINANCE_COMPANY_LEASING:
      return ' kr/mån (exkl. moms)';

    case FINANCE_RESERVATION:
      return ' kr (inkl. moms)';

    case FINANCE_SMART_LOAN:
      return ' kr/mån (inkl. moms)';

    default:
      return ' kr';
  }
};

export const getTotalFullPrice = (price, checkoutType, addons, skipSuffix) => {
  const financeTypeAddonField = getPriceTypeFromCheckoutType(checkoutType);
  const suffix = getPriceSuffix(checkoutType);
  const chosenAddons = addons.filter((addon) => addon.chosen === true);

  if (chosenAddons.length > 0) {
    let addonsTotalPrice = 0;
    chosenAddons.map((addon) => {
      if (addon[financeTypeAddonField]) {
        addonsTotalPrice += Number(addon[financeTypeAddonField]);
      }
    });

    if (skipSuffix) {
      return Number(price) + Number(addonsTotalPrice);
    }

    return formatPrice(Number(price) + Number(addonsTotalPrice)) + suffix;
  }

  if (skipSuffix) {
    return price;
  }

  return formatPrice(price) + suffix;
};

const getAddonsFromCheckoutType = (financeType, checkoutState) => {
  switch (financeType) {
    case FINANCE_PRIVATE_LEASING:
      return checkoutState?.privateLeasingAddons.map((item) => ({
        ...item,
        price: item.monthlyCost ? item.monthlyCost : item?.price,
      }));

    case FINANCE_COMPANY_LEASING:
      return checkoutState?.addons.map((item) => ({
        ...item,
        price: item.monthlyCost ? item.monthlyCost : item?.price,
      }));

    case FINANCE_SMART_LOAN:
      return checkoutState?.addons.map((item) => ({
        ...item,
        price: item.monthlyCost ? item.monthlyCost : item?.price,
      }));

    default:
      return checkoutState?.addons;
  }
};

const prepareDataToBeSent = (checkoutState) => {
  const newCheckoutState = { ...checkoutState };
  const financeType = newCheckoutState.chosenCheckoutType;
  const newAddons = (getAddonsFromCheckoutType(financeType, newCheckoutState) || []).filter(
    (item) => item.chosen
  );
  delete newCheckoutState.meta;
  delete newCheckoutState.privateLeasingAddons;
  delete newCheckoutState.checkoutChanges;
  newCheckoutState.addons = newAddons;

  return newCheckoutState;
};

export const submitCheckout = (checkoutState) => {
  const newCheckoutState = prepareDataToBeSent(checkoutState);

  return fetch('/apicommerce/HedinBil/orderVehicle', {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(newCheckoutState),
  }).then((res) => res.json());
};

export const submitViaResursCheckout = (checkoutState) => {
  const newCheckoutState = prepareDataToBeSent(checkoutState);

  return fetch('/apicommerce/HedinBil/orderVehicleViaResurs', {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(newCheckoutState),
  }).then((res) => res.json());
};
